import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { reviews, mockReviewSubmit } from '../mock';
import { Star, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const Reviews = () => {
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    service: '',
    comment: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.comment || rating === 0) {
      toast.error('Please fill in all fields and select a rating');
      return;
    }

    const reviewData = {
      ...formData,
      rating
    };

    mockReviewSubmit(reviewData);
    toast.success('Thank you for your review! It will be published after approval.');
    
    // Reset form
    setFormData({ name: '', service: '', comment: '' });
    setRating(0);
    setShowForm(false);
  };

  return (
    <section id="reviews" className="py-20 px-6 bg-zinc-950">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-4 uppercase tracking-tight">
            Client <span className="text-red-600">Reviews</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            Hear from our satisfied clients
          </p>
          
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 font-bold uppercase tracking-wider"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Leave a Review
          </Button>
        </div>

        {/* Review Form */}
        {showForm && (
          <Card className="bg-zinc-900 border-zinc-800 max-w-2xl mx-auto mb-12">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-white font-bold uppercase text-sm">Your Rating *</Label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="transition-transform duration-200 hover:scale-110"
                      >
                        <Star
                          className={`w-8 h-8 ${
                            star <= (hoverRating || rating)
                              ? 'fill-red-600 text-red-600'
                              : 'text-gray-600'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="review-name" className="text-white font-bold uppercase text-sm">
                      Your Name *
                    </Label>
                    <Input
                      id="review-name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-red-600"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="review-service" className="text-white font-bold uppercase text-sm">
                      Service Received
                    </Label>
                    <Input
                      id="review-service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      placeholder="Tattooing, Piercing, etc."
                      className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-red-600"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="review-comment" className="text-white font-bold uppercase text-sm">
                    Your Review *
                  </Label>
                  <Textarea
                    id="review-comment"
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    placeholder="Share your experience with us..."
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 min-h-32 focus:border-red-600"
                    required
                  />
                </div>

                <div className="flex gap-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider"
                  >
                    Submit Review
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    variant="outline"
                    className="flex-1 border-zinc-700 text-white hover:bg-zinc-800"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review) => (
            <Card key={review.id} className="bg-zinc-900 border-zinc-800 hover:border-red-600 transition-all duration-300">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < review.rating ? 'fill-red-600 text-red-600' : 'text-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-gray-500 text-sm">
                    {new Date(review.date).toLocaleDateString()}
                  </span>
                </div>

                <p className="text-gray-300 leading-relaxed mb-4">
                  "{review.comment}"
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                  <div>
                    <p className="text-white font-bold">{review.name}</p>
                    <p className="text-red-500 text-sm uppercase tracking-wider">{review.service}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
